import { ReactNode } from 'react';
import * as moment from 'moment';
import { EventSourceIcon } from './events/EventSourceIcon';
import { Dictionary } from 'lodash';
import { CallEventDetails } from './events/CallEventDetails';
import { CallId, VideoCallOfferEvent, VideoCallRejectEvent, VideoCallChatEvent, VideoCallButtonEvent, VideoCallOverrideEvent } from 'shared/models';
import { MSCallEventDetails } from './events/MSCallEventDetails';
import { LPCallEventDetails } from './events/LPCallEventDetails';
import { OfferEventDetails } from './events/OfferEventDetails';
import { CallIdEventDetails } from './events/CallIdEventDetails';
import { RejectEventDetails } from './events/RejectEventDetails';
import { useSelector } from 'react-redux';
import { State } from 'reducers';
import { getVideoCall } from 'selectors/ecsSelectors';
import { ChatEventDetails } from './events/ChatEventDetails';
import { ButtonEventDetails } from './events/ButtonEventDetails';
import { HelpIndicatorEventDetails } from './events/HelpIndicatorEventDetails';
import { GoodbyeEventDetails } from './events/GoodbyeEventDetails';
import { OverrideEventDetails } from './events/OverrideEventDetails';

require('./VideoCallEventRecord.css');

export type Props = {
    id: number;
    callId: CallId;
    type: string;
    details: Record<string, unknown>;
    timestamp: number;
};

const sourceByType: Dictionary<'EL' | 'LP' | 'MS' | 'M1'> = {
    CALL: 'EL',
    MSCL: 'MS',
    LPCL: 'LP',
    OFFR: 'EL',
    RJCT: 'EL',
    JPEG: 'EL',
    CLID: 'M1',
    //CHAT: // Can be LP or MS
    BTTN: 'EL',
    HLPA: 'LP',
    HLPC: 'MS',
    //GBYE: // Can be LP or MS
    OVRD: 'LP',
};

function getDetailsNode(callId: CallId, type: string, details: Record<string, unknown>): ReactNode {
    switch (type) {
        case 'CALL': return <CallEventDetails callId={ callId }/>;
        case 'MSCL': return <MSCallEventDetails callId={ callId }/>;
        case 'LPCL': return <LPCallEventDetails callId={ callId }/>;
        case 'CLID': return <CallIdEventDetails callId={ callId }/>;
        case 'OFFR': return <OfferEventDetails callId={ callId } details={ details as VideoCallOfferEvent['details'] }/>;
        case 'RJCT': return <RejectEventDetails callId={ callId } details={ details as VideoCallRejectEvent['details'] }/>;
        case 'CHAT': return <ChatEventDetails callId={ callId } details={ details as VideoCallChatEvent['details'] } />;
        case 'BTTN': return <ButtonEventDetails callId={ callId } details={ details as VideoCallButtonEvent['details'] } />;
        case 'HLPA': return <HelpIndicatorEventDetails callId={ callId } type={ type } />;
        case 'HLPC': return <HelpIndicatorEventDetails callId={ callId } type={ type } />;
        case 'OVRD': return <OverrideEventDetails callId={ callId } details={ details as VideoCallOverrideEvent['details'] }/>;
        case 'GBYE': return <GoodbyeEventDetails />;
    }
    return null;
}

export const VideoCallEventRecord = (props: Props) => {
    const { id, callId, type, details, timestamp } = props;

    const videoCall = useSelector((state: State) => getVideoCall(state, props));

    const source = sourceByType[type];
    const respondent =
        videoCall && videoCall.joined
            ? videoCall.userId
                ? 'MS'
                : 'LP'
            : undefined;

    const detailsNode = getDetailsNode(callId, type, details);
    return (
        <div
            id={ `vcer-${id}` }
            className="vcer"
            data-type={ type }
        >
            <div className="time">
                { moment.utc(timestamp).local().format('LTS') }
            </div>

            <div className="source">
                <EventSourceIcon source={ source || respondent }/>
            </div>

            <div className="details">
                { detailsNode }
            </div>
        </div>
    );
};
